import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { LayoutService } from './core/services/layout.service';
import { Layout } from './shared/models/layout.model';
import { DopAuthService } from '../../projects/dop-core/src/lib/security/dop-auth.service';
import { AuthGuardMessageService } from './core/guards/auth-guard.message.service';
import { InterceptorMessageService, StoreService, TranslationService } from 'projects/dop-core/src/public-api';
import { registerLocaleData } from '@angular/common';
import { Router, Route } from '@angular/router';
import { routes } from './app-routing.module';
import { StoreKey } from './core/store/ngrx-store.keys';
import { UserDetails } from './shared/models/user-details.model';
import { AppConfigService } from './core/app-config/app-config.service';
import { SettingsService } from './shared/data-service/settings.service';
import { SettingFeatureFlags, SettingConfigurations } from 'projects/dop-menu/src/public-api';
import { ErrorDetails } from './shared/models/error.modal';
import { InactivityService } from './inactivity.service';
import { DopMenuService } from 'projects/dop-menu/src/lib/dop-menu.service';
import { BabelService } from './shared/data-service/babel.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {

  title = 'configuration';
  layout: Layout = {
    pageHeader: {
      pageTitle: '',
      pageIcon: '',
      iconLink: '',
      confirmDialogRequired: false,
    },
    breadcrumbItems: []
  } as Layout;
  loading = true;
  openAccessDeniedDialog = false;
  isUserDataLoaded = false;
  userDetails: UserDetails;
  eolWebsiteUrl: string;
  featureFlags: SettingFeatureFlags;
  configurations: SettingConfigurations;
  isFeatureFlagLoaded = false;
  error: ErrorDetails = new ErrorDetails();
  hideHeader = false;

  constructor(
    private layoutService: LayoutService,
    private cdr: ChangeDetectorRef,
    private interceptorMsgService: InterceptorMessageService,
    public dopAuthService: DopAuthService,
    private authGuardMessageService: AuthGuardMessageService,
    private router: Router,
    private storeService: StoreService,
    private appConfigService: AppConfigService,
    private settingsService: SettingsService,
    private babelService: BabelService,
    private readonly inactivityService: InactivityService,
    private readonly dopMenuService: DopMenuService
  ) { }

  setErrorData() {
    this.error.errorDialogHeader = TranslationService.translate('vbeiGeneralErrorHeader'),
      this.error.errorMessage = TranslationService.translate('vbeiGeneralErrorMessage'),
      this.error.displayError = true;
  }

  async ngOnInit() {
    await this.babelService.getTranslations();
    this.inactivityService.onTimeOut.asObservable().subscribe(res => {
      this.dopMenuService.logOut();
    });

    this.interceptorMsgService.isLoading.subscribe(data => {
      this.loading = data;
    });
    this.interceptorMsgService.generalError.subscribe((data) => {
      if (data) {
        this.setErrorData();
      }
    });

    if (this.dopAuthService.isLoggedIn) {
    await this.settingsService.loadSettings();
    }
    this.eolWebsiteUrl = this.appConfigService.configuration.eolWebsiteUrl;
    const language = sessionStorage.getItem('LanguageCode') ? sessionStorage.getItem('LanguageCode'): navigator.language.split('-')[0];
    this.localeInitializer(language);
    this.layoutService.layoutChanges.subscribe((layout: Layout) => {
      this.layout = layout;
      this.cdr.detectChanges();
    });

    this.authGuardMessageService.userDeniedPermission.subscribe((data: boolean) => {
      this.openAccessDeniedDialog = data;
    });
    this.authGuardMessageService.userDataLoaded.subscribe(isLoaded => {
      this.isUserDataLoaded = isLoaded;
      if (isLoaded) {
        this.getUserDetails();
        this.getSettingsDetails();
      }
    });
    this.loading = false;

    const pathName = window.location.pathname;
    if (pathName.match(/^\/\S+\/\S+/)) {
      this.navigateToModuleDefaultPage(pathName.split('/'));
    }

    // for downlaod file
    if (window.localStorage.getItem('internalFileName')?.length > 0) {
      this.router.navigate(['/downloadFile/']);
      this.hideHeader = true;
    }

    // for order summary
    if (window.location.hash.includes('/guided')) {
      this.router.navigate(['']);
    }

    // When accessToken is removed (i.e. on logout)
    // Reload the site opened in other tabs
    window.addEventListener('storage', (e) => {
      if (e.key === 'access_token' && e.newValue === null) {
        location.reload();
      }
    });
  }

  navigateToModuleDefaultPage(paths: string[]): void {
    // Checking for each path as we included /dev in for develop branch
    for (const path of paths) {
      const isRouteFound = routes.some((route: Route) => route.path === path);
      if (isRouteFound) {
        this.router.navigate([path]);
        break;
      }
    }
  }

  isLoggedIn(): boolean {
    return this.dopAuthService.isLoggedIn;
  }

  closeErrorDialog() {
    this.openAccessDeniedDialog = false;
  }

  closeDialogBox() {
    this.error.displayError = false;
    this.interceptorMsgService.isLoading.next(false);
  }

  onActivate(event: Event) {
    const scrollToTop = window.setInterval(() => {
      const pos = window.pageYOffset;
      if (pos > 0) {
        window.scrollTo(0, pos - 20); // how far to scroll on each step
      } else {
        window.clearInterval(scrollToTop);
      }
    }, 16);
  }

  localeInitializer(language: string): Promise<any> {
    return import(
      // We should add the languages which needs to be supported in the below comment,
      // creates chunks of all languages if removed
      /* webpackInclude: /\.js$/ */
      `@angular/common/locales/${language}.js`
    ).then(module => registerLocaleData(module.default));
  }

  getUserDetails() {
    this.storeService.Get<any>(StoreKey.UserDetails).subscribe(data => {
      this.userDetails = data;
    });
  }

  getSettingsDetails() {
    this.featureFlags = this.settingsService.FeatureFlags;
    this.configurations = this.settingsService.Configurations;
    this.configurations.myResultsNewUrl = this.appConfigService.configuration.myResultsNewUrl;
    this.isFeatureFlagLoaded = true;
  }

}
